import { useState, useEffect, useRef, useMemo, useId } from 'react';
import { Search, X, CornerDownLeft } from 'lucide-react';

/**
 * Buscador global de MiPAE — estilo command palette. Filtra una lista plana
 * de destinos (secciones, escalas clínicas, PAEs guardados) y los ejecuta.
 *
 * El componente es agnóstico al contenido: App.jsx arma `items` y este modal
 * solo busca, agrupa y navega. Búsqueda insensible a mayúsculas y tildes.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {Array<{
 *   id: string,
 *   group: string,
 *   label: string,
 *   sub?: string,
 *   icon?: React.ReactNode,
 *   keywords?: string[],
 *   onSelect: () => void,
 * }>} props.items
 */

const normalize = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export default function CommandPalette({ open, onClose, items = [] }) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const titleId = useId();

  // Al abrir: limpiar query, resetear selección, enfocar el input.
  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setActiveIdx(0);
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  // Bloquear scroll del body mientras está abierto.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const results = useMemo(() => {
    const q = normalize(query.trim());
    // Sin búsqueda solo se muestran las secciones; el contenido (GES, fármacos)
    // aparece al escribir, para no saturar el estado inicial.
    if (!q) return items.filter((it) => !it.searchOnly);
    const tokens = q.split(/\s+/);
    return items.filter((it) => {
      const haystack = normalize(
        [it.label, it.sub, ...(it.keywords || [])].filter(Boolean).join(' ')
      );
      return tokens.every((tok) => haystack.includes(tok));
    });
  }, [items, query]);

  // Mantener activeIdx dentro de rango cuando cambian los resultados.
  useEffect(() => {
    setActiveIdx((i) => Math.min(i, Math.max(0, results.length - 1)));
  }, [results.length]);

  // Desplazar el item activo a la vista durante navegación con teclado.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const groups = useMemo(() => {
    const gs = [];
    results.forEach((it, idx) => {
      let g = gs.find((x) => x.title === it.group);
      if (!g) {
        g = { title: it.group, items: [] };
        gs.push(g);
      }
      g.items.push({ ...it, _idx: idx });
    });
    return gs;
  }, [results]);

  if (!open) return null;

  const select = (item) => {
    if (!item) return;
    onClose();
    item.onSelect();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(results[activeIdx]);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]">
      <div
        className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        className="relative w-full max-w-lg bg-paper border border-edge rounded-tk-lg shadow-tk-modal overflow-hidden flex flex-col max-h-[70vh]"
      >
        <span id={titleId} className="sr-only">
          Buscar en MiPAE
        </span>

        {/* Campo de búsqueda */}
        <div className="flex items-center gap-2.5 px-4 border-b border-edge flex-shrink-0">
          <Search
            size={18}
            strokeWidth={1.7}
            className="text-ink-muted flex-shrink-0"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
            placeholder="Buscar GES, fármaco, escala, sección…"
            aria-label="Buscar en MiPAE"
            autoComplete="off"
            spellCheck="false"
            className="flex-1 min-w-0 bg-transparent py-3.5 text-16px text-ink font-sans placeholder:text-ink-faint focus:outline-none"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar buscador"
            className="flex-shrink-0 text-ink-muted hover:text-ink transition-colors p-1 -m-1"
          >
            <X size={18} strokeWidth={1.6} aria-hidden="true" />
          </button>
        </div>

        {/* Resultados */}
        <div
          ref={listRef}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-2"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {results.length === 0 ? (
            <p className="px-4 py-10 text-center text-small text-ink-muted font-sans">
              Sin resultados para “{query.trim()}”.
            </p>
          ) : (
            groups.map((g) => (
              <div key={g.title} className="mb-1">
                <p className="text-micro text-ink-muted px-4 pt-2 pb-1 font-sans">
                  {g.title}
                </p>
                {g.items.map((it) => {
                  const isActive = it._idx === activeIdx;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      data-idx={it._idx}
                      onClick={() => select(it)}
                      onMouseMove={() => setActiveIdx(it._idx)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        isActive ? 'bg-surface' : 'bg-transparent'
                      }`}
                    >
                      {it.icon && (
                        <span className="flex-shrink-0 text-ink-muted flex items-center">
                          {it.icon}
                        </span>
                      )}
                      <span className="flex-1 min-w-0">
                        <span className="block text-body text-ink font-sans truncate">
                          {it.label}
                        </span>
                        {it.sub && (
                          <span className="block text-small text-ink-muted font-sans truncate">
                            {it.sub}
                          </span>
                        )}
                      </span>
                      {isActive && (
                        <CornerDownLeft
                          size={14}
                          strokeWidth={1.6}
                          className="text-ink-faint flex-shrink-0"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
